import { MoreVertical } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { formatDateTime } from '@/utils/format'
import { Switch } from 'antd'
import type { CombinedColumnDef, StyleConfig } from '@/api/dowload-excel.api'

export interface RobotEventRow {
  id: string
  eventId: string
  name: string
  startDate?: string
  endDate?: string
  startTime?: string
  endTime?: string
  priority?: number
  repeatType?: number
  repeatValue?: string
  allowRepeat: number
  eventType: 'Khẩn cấp' | 'Lập lịch'
  status: boolean
  active: boolean
}

// Bảng màu Excel cho LOẠI SỰ KIỆN
const EVENT_TYPE_EXCEL_STYLES: Record<string, StyleConfig> = {
  'Khẩn cấp': {
    backgroundColor: '#FEECEC',
    textColor: '#F05A5A',
    bold: true,
  },
  'Lập lịch': {
    backgroundColor: '#E7F2FF',
    textColor: '#2D92F2',
    bold: true,
  },
}

// Bảng màu Excel cho TRẠNG THÁI SỰ KIỆN & HOẠT ĐỘNG
const STATUS_EXCEL_STYLES: Record<string, StyleConfig> = {
  'Hoạt động': {
    backgroundColor: '#E6F7EE',
    textColor: '#2EAF62',
    bold: true,
  },
  'Không hoạt động': {
    backgroundColor: '#FDEBEC',
    textColor: '#F05A5A',
    bold: true,
  },
  Bật: {
    backgroundColor: '#E6F7EE',
    textColor: '#2EAF62',
    bold: true,
  },
  Tắt: {
    backgroundColor: '#FDEBEC',
    textColor: '#F05A5A',
    bold: true,
  },
}

function getTypeBadgeClass(type: RobotEventRow['eventType']) {
  return type === 'Khẩn cấp'
    ? 'bg-[#FEECEC] text-[#F05A5A]'
    : 'bg-[#E7F2FF] text-[#2D92F2]'
}

function getStatusBadgeClass(isActive: boolean) {
  return isActive
    ? 'bg-[#E6F7EE] text-[#2EAF62]'
    : 'bg-[#FDEBEC] text-[#F05A5A]'
}

interface BuildRobotEventColumnsOptions {
  pageIndex: number
  pageSize: number
  onViewDetail?: (event: RobotEventRow) => void
  onUpdate?: (event: RobotEventRow) => void
  onDelete?: (event: RobotEventRow) => void
  onToggleStatus?: (event: RobotEventRow) => void
}

export function buildRobotEventColumns({
  pageIndex,
  pageSize,
  onViewDetail,
  onUpdate,
  onDelete,
  onToggleStatus,
}: BuildRobotEventColumnsOptions): CombinedColumnDef<RobotEventRow>[] {
  return [
    {
      id: 'stt',
      header: 'STT',
      size: 55,
      align: 'center',
      cell: ({ row }) => row.index + 1 + pageIndex * pageSize,
    },
    {
      accessorKey: 'name',
      header: 'SỰ KIỆN',
      align: 'left',
      cell: ({ row }) => (
        <button
          type="button"
          className="cursor-pointer text-[#CE7A58] hover:text-[#b8694a]"
          onClick={() => onViewDetail?.(row.original)}
        >
          {row.original.name}
        </button>
      ),
    },
    {
      accessorKey: 'startDate',
      header: 'BẮT ĐẦU',
      size: 110,
      align: 'center',
      cell: ({ getValue }) => formatDateTime(getValue(), 'DD/MM/YYYY'),
    },
    {
      accessorKey: 'endDate',
      header: 'KẾT THÚC',
      size: 110,
      align: 'center',
      cell: ({ getValue }) => formatDateTime(getValue(), 'DD/MM/YYYY'),
    },
    {
      accessorKey: 'eventType',
      header: 'LOẠI SỰ KIỆN',
      size: 110,
      align: 'center',
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${getTypeBadgeClass(
            row.original.eventType,
          )}`}
        >
          {row.original.eventType}
        </span>
      ),
      exportStyle: { align: 'center' },
      exportStatusStyles: EVENT_TYPE_EXCEL_STYLES,
    },
    {
      accessorKey: 'status',
      header: 'TRẠNG THÁI SỰ KIỆN',
      size: 140,
      align: 'center',
      cell: ({ row }) => (
        <span
          className={`inline-flex rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${getStatusBadgeClass(
            row.original.status,
          )}`}
        >
          {row.original.status ? 'Hoạt động' : 'Không hoạt động'}
        </span>
      ),
      exportStyle: { align: 'center' },
      exportStatusStyles: STATUS_EXCEL_STYLES,
    },
    {
      accessorKey: 'active',
      header: 'TRẠNG THÁI HOẠT ĐỘNG',
      size: 140,
      align: 'center',
      cell: ({ row }) => {
        const event = row.original
        return (
          <div className="flex justify-center">
            <Switch
              checked={event.active}
              onChange={() => onToggleStatus?.(event)}
            />
          </div>
        )
      },
    },
    {
      id: 'actions',
      size: 55,
      exportable: false,
      cell: ({ row }) => (
        <div className="flex justify-center">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreVertical size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-32 text-xs">
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => onViewDetail?.(row.original)}
              >
                Xem chi tiết
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer text-[#CE7A58]"
                onClick={() => onUpdate?.(row.original)}
              >
                Cập nhật
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => onDelete?.(row.original)}
              >
                Xóa
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ]
}
